import { RefObject, useEffect, useRef, useState } from 'react';

type UseReferenceChipsSelectAllShortcutArgs = {
  // DOM scopes the shortcut is bound to; the hook acts when focus is inside any of them. Multiple scopes let the popover (portal-rendered, outside the input wrapper) share the shortcut.
  scopeRefs: RefObject<HTMLElement | null>[];
  // True when there is at least one chip to select — Ctrl+A is a no-op on an empty list.
  hasChips: boolean;
  disabled?: boolean;
  // Called when the user confirms the "select all" by pressing Backspace or Delete afterwards.
  onClearAll: () => void;
};

// Two-step "select all chips → Backspace/Delete clears" shortcut. Ctrl/Cmd+A inside any scope flips the visual select-all state; the next Backspace or Delete triggers onClearAll; any other key or a click resets the state.
export const useReferenceChipsSelectAllShortcut = ({
  scopeRefs,
  hasChips,
  disabled = false,
  onClearAll,
}: UseReferenceChipsSelectAllShortcutArgs) => {
  const [selectedAll, setSelectedAll] = useState(false);
  // Mouse position falls back when no focused element is inside scope (cursor hovering over the popup but focus parked on body etc.).
  const lastHoveredTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    const isCandidateInScope = (candidate: Node | null) => {
      if (!candidate) return false;
      return scopeRefs.some((ref) => ref.current?.contains(candidate) ?? false);
    };

    const isInsideScope = () => {
      if (isCandidateInScope(document.activeElement)) return true;
      return isCandidateInScope(lastHoveredTargetRef.current as Node | null);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;
      if (!isInsideScope()) {
        if (selectedAll) setSelectedAll(false);
        return;
      }

      const isSelectAll =
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'a';

      if (isSelectAll) {
        const active = document.activeElement;
        const isEditableTarget =
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement;
        // With focus in the chip input the browser default selects the typed text — wanted, the visual select-all covers chips and text alike. Everywhere else preventDefault blocks the page-wide fallback selection.
        if (!isEditableTarget) event.preventDefault();
        if (!hasChips) return;
        setSelectedAll(true);
        return;
      }

      if (
        selectedAll &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        event.preventDefault();
        event.stopPropagation();
        onClearAll();
        setSelectedAll(false);
        return;
      }

      // Any other key while the visual select-all is active drops it so single-chip editing resumes.
      if (selectedAll) setSelectedAll(false);
    };

    // Click anywhere dismisses the visual select-all (mirrors how OS lists deselect on click).
    const onClick = () => {
      if (selectedAll) setSelectedAll(false);
    };

    const onMouseMove = (event: MouseEvent) => {
      lastHoveredTargetRef.current = event.target;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('click', onClick);
    window.addEventListener('mousemove', onMouseMove, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('click', onClick);
      window.removeEventListener('mousemove', onMouseMove, true);
    };
  }, [scopeRefs, hasChips, disabled, onClearAll, selectedAll]);

  // For sibling keyboard features that claim a key before the window listener sees it (capture-phase stopPropagation) — they must drop the visual select-all themselves.
  const clearSelectedAll = () => setSelectedAll(false);

  return { selectedAll, clearSelectedAll };
};
